/**
 * Types for the structured pipeline editor.
 *
 * Mirrors docs/constantine-module-metadata-spec.md. The manifest shape is
 * the contract between Constantine (which generates manifest.json at
 * sidecar-build time) and Guard's editor. Keep this file 1:1 with the spec
 * — if a field moves, both sides change together.
 *
 * Nothing here is Constantine-specific. The same types render any
 * capability whose options endpoint returns a manifest of this shape.
 */

// ── Cost model primitives ─────────────────────────────────────────────────

export type CostPrimitiveType =
  | 'fixed'
  | 'per_unit'
  | 'per_unit_ensemble'
  | 'bounded_agentic'
  | 'composite';

export interface CostCalibration {
  experiment_id: string | null;
  date?: string;
  n_runs?: number;
  notes?: string;
  blocker?: string;
  /**
   * Observed p90/p10 cost-ratio across the calibration runs. Encodes
   * empirical spread separately from `confidence` (which is n-runs-trust).
   * When present, the editor uses log-normal bounds:
   *   low  = p50 / sqrt(ratio)
   *   high = p50 × sqrt(ratio)
   * Yielding an 80% prediction interval. Absent → editor falls back to
   * confidence-derived ±(1−confidence)/2 spread, which is appropriate
   * for newly-calibrated rates without observed variance yet.
   *
   * See agent-bridge thread #66/67 for why this field exists separately
   * from `confidence`.
   */
  observed_p10_p90_ratio?: number;
}

export interface FixedCost {
  type: 'fixed';
  name?: string;
  amount_usd: number | null;
  confidence: number;
  calibration?: CostCalibration;
}

export interface PerUnitRate {
  input_tokens_per_unit: number | null;
  output_tokens_per_unit: number | null;
  /**
   * Optional cache-tier rates. When the calibrated module exercises
   * Anthropic-style (or equivalent) prompt caching, calibrate the raw
   * cache_write / cache_read token counts here separately from fresh
   * input. The pricing layer multiplies each by the provider's
   * per-token cache rate (ci/cr from dist/model-pricing.json). Modules
   * that don't use caching omit these fields; treated as 0.
   */
  cache_creation_tokens_per_unit?: number | null;
  cache_read_tokens_per_unit?: number | null;
}

export interface PerUnitCost {
  type: 'per_unit';
  name?: string;
  driver: string;
  model_id?: string;
  model_tier?: ModelTier;
  rate: PerUnitRate;
  confidence: number;
  calibration?: CostCalibration;
}

export interface PerUnitEnsembleCost {
  type: 'per_unit_ensemble';
  name?: string;
  driver: string;
  models: string;
  rate: PerUnitRate;
  confidence: number;
  calibration?: CostCalibration;
}

export interface BoundedAgenticCost {
  type: 'bounded_agentic';
  name?: string;
  /** Cap on iterations — integer literal or a driver expression string. */
  max_iterations: number | string;
  /** Telemetry-derived p50 iteration count, or null when uncalibrated. */
  expected_iterations: number | string | null;
  /** Per-iteration token rate — same shape as PerUnitRate. */
  per_iteration: PerUnitRate;
  model_id?: string;
  model_tier?: ModelTier;
  confidence: number;
  calibration?: CostCalibration;
}

export type CostLeaf =
  | FixedCost
  | PerUnitCost
  | PerUnitEnsembleCost
  | BoundedAgenticCost;

export interface CompositeCost {
  type: 'composite';
  components: CostLeaf[];
}

export type CostModel = CostLeaf | CompositeCost;

// ── Module shape (one entry per module.yaml) ──────────────────────────────

export type ModelTier = 'fast' | 'standard' | 'deep';

export type PipelineStage =
  | 'ingest'
  | 'detect'
  | 'review'
  | 'skeptic'
  | 'report'
  | 'exploit';

export type ConfigKnobType =
  | 'model_ref'
  | 'int'
  | 'float'
  | 'string'
  | 'enum'
  | 'bool'
  | 'list';

export interface ConfigKnob {
  type: ConfigKnobType;
  default: string | number | boolean | string[] | null;
  description?: string;
  min?: number;
  max?: number;
  /**
   * Allowed values when `type === 'enum'`. Constantine ships the manifest
   * with `enum: [...]` as the literal field name (matches JSON Schema
   * convention). Older code may have used `options` — accepted for
   * back-compat, but new manifests should use `enum`.
   */
  enum?: string[];
  options?: string[];
  /** When type === 'list', the element type. */
  item_type?: ConfigKnobType;
}

export interface ModuleBench {
  dataset: string;
  metric: string;
  value: number;
  date?: string;
}

export interface BestForTag {
  language?: string;
  surface?: string;
  asset_class?: string;
}

/**
 * A single typed output a module emits into the downstream chain. Each
 * entry in `ModuleManifest.emits` describes ONE output key (`bytes`,
 * `findings_count`, etc) with a driver expression + rate. The evaluator
 * walks the pipeline in stage order: for each module's emits[key], it
 * adds `eval(driver) * rate` into the running sum at `input.<key>`,
 * which downstream modules read in their cost_model driver expressions.
 *
 * Replaces the prior special-cased `findings_projection` (detect-only)
 * and `output_bytes_projection` (ingest-only) fields with a generic
 * any-module-emits-any-key chain. Constantine ships this at
 * manifest_version >= constantine-manifest@e6509a9f56fe (commit 6cf269f).
 */
export interface ModuleEmit {
  /** Driver expression evaluated against target.* / module.* / input.* */
  driver: string;
  /** Units of <key> per unit of the driver. */
  rate: number;
  /** 0..1; spread band on the editor's bounds. */
  confidence: number;
  calibration?: CostCalibration;
}

export interface ModuleManifest {
  name: string;
  version: string;
  display_name: string;
  stage: PipelineStage | PipelineStage[];
  /** Executable invoked by the framework (e.g. "run.sh"). */
  entrypoint?: string;
  description: string;

  /**
   * Editor visibility flag. `false` means the module is uncalibrated and/or
   * has known integration bugs — it should NOT appear in the swap / add
   * module-picker, but if a custom-uploaded pipeline already references it
   * we keep the row rendered with a warning chip (don't silently drop user
   * config). `true` means the module is fully exposable. The decision is
   * owned by constantine — modules graduate to `guard: true` after their
   * cost rates are calibrated and their integration paths are validated.
   */
  guard: boolean;

  best_for: BestForTag[];
  trade_offs?: string;
  compatible_with: string[];
  incompatible_with: string[];

  model_tiers_used: ModelTier[];
  config_knobs: Record<string, ConfigKnob>;

  /**
   * Optional filter rule key (matches an entry in
   * `dist/filter-rules.json#filters`). When set, this module operates
   * on the FILTERED byte stream — the cost estimator overrides
   * `target.size_bytes` to `target.filtered_bytes_by_filter[filter_id]`
   * for this module's driver evaluation. Pass-through ingests
   * (git-clone, deltascan-repo) leave this unset and drive on raw bytes.
   */
  filter_id?: string;

  cost_model: CostModel;
  /**
   * Optional typed outputs that flow into downstream modules' input.*
   * namespace. Ingest modules emit `bytes`; detect modules emit
   * `findings_count`; reviewer modules emit a confirmation-filtered
   * `findings_count`; etc. The evaluator sums per-key across upstream
   * modules in pipeline order; downstream cost_model.driver expressions
   * read the accumulated value via `input.<key>`.
   */
  emits?: Record<string, ModuleEmit>;
  bench?: ModuleBench;
}

// ── Pipeline-level shape (one selected pipeline = an editable doc) ────────

/**
 * A module reference inside a stage. Mirrors constantine's pipelineModule
 * shape: a module can appear as a bare string ("scored-functions") OR as
 * an object with overrides ({ name, llm?, fallback?, timeout? }). YAML
 * round-trip preserves whichever shape the user authored; use
 * `getModuleRefName()` to read the name uniformly.
 */
export interface ModuleRefObject {
  name: string;
  fallback?: string;
  timeout?: number;
  llm?: {
    provider?: string;
    models?: Partial<Record<ModelTier, string>>;
    budget?: number;
  };
  // Arbitrary additional fields the schema doesn't know about. Preserved
  // on save so unknown keys round-trip — matches the backend's generic-map
  // rewrite in rewritePipelineConfig.
  [extra: string]: unknown;
}

export type ModuleRef = string | ModuleRefObject;

export function getModuleRefName(ref: ModuleRef): string {
  return typeof ref === 'string' ? ref : ref.name;
}

export function toModuleRefObject(ref: ModuleRef): ModuleRefObject {
  return typeof ref === 'string' ? { name: ref } : ref;
}

export interface StageDoc {
  name: PipelineStage | string;
  modules: ModuleRef[];
  timeout?: number;
}

export interface PipelineLLM {
  /**
   * Routing layer for the actual LLM calls. Informational for the editor
   * — pricing is resolved purely from each leaf's `model_id`, which
   * should already be routed (e.g. `bedrock/us.anthropic.claude-haiku-4-5-20251001-v1:0`)
   * when the user is on a non-direct provider. The earlier "pricing_overlay"
   * multiplier system was removed after constantine confirmed real Bedrock
   * vs direct premium is ~10% (LiteLLM table), not the ~3x our earlier
   * back-calculation suggested — that 3x was a reverse-engineered
   * artifact of a constantine SDK bug.
   */
  provider?: string;
  models?: Partial<Record<ModelTier, string>>;
  budget?: number;
  /**
   * Currently a NO-OP. Constantine is always invoked with the
   * `--enforce-budget` CLI flag on every run path (main scan, patch,
   * validate, exploit), so the per-scan budget is always enforced as a
   * hard cap regardless of this field's value. The flag is passed
   * unconditionally and this `enforce_budget` field is parsed but never
   * read to gate it, so toggling it changes nothing at runtime. Retained
   * only for forward/round-trip compatibility of the pipeline doc.
   */
  enforce_budget?: boolean;
}

export interface PipelineDoc {
  tier?: string;
  cost_indicator?: string;
  llm: PipelineLLM;
  pipeline: StageDoc[];
  module_config?: Record<string, Record<string, unknown>>;
  bench?: {
    dataset?: string;
    scanner_config?: string;
    hit?: number;
    attempted?: number;
    pass_rate?: number;
    notes?: string;
  };
  // Round-trip unknown top-level fields. Matches backend rewrite behavior.
  [extra: string]: unknown;
}

// ── Module compatibility ─────────────────────────────────────────────────

/**
 * Reasons a candidate module can't be added alongside the current siblings.
 * Returned by `checkCompatibility` so callers can surface a chip with the
 * specific blocker rather than a generic "not compatible."
 */
export type IncompatibilityReason =
  | { kind: 'candidate_excludes_sibling'; siblingName: string }
  | { kind: 'sibling_excludes_candidate'; siblingName: string };

/**
 * Symmetric incompatibility check: a candidate module cannot be added if
 *   (a) candidate.incompatible_with contains a current sibling's name, OR
 *   (b) any current sibling's incompatible_with contains the candidate.
 *
 * Returns null when the candidate is compatible with every sibling; otherwise
 * returns the first conflict found (intentional — the picker only needs to
 * render one reason per row).
 */
export function checkCompatibility(
  candidate: ModuleManifest,
  siblings: ModuleManifest[]
): IncompatibilityReason | null {
  const candidateExcludes = new Set(candidate.incompatible_with ?? []);
  for (const sibling of siblings) {
    if (candidateExcludes.has(sibling.name)) {
      return { kind: 'candidate_excludes_sibling', siblingName: sibling.name };
    }
    const siblingExcludes = new Set(sibling.incompatible_with ?? []);
    if (siblingExcludes.has(candidate.name)) {
      return { kind: 'sibling_excludes_candidate', siblingName: sibling.name };
    }
  }
  return null;
}

// ── Manifest envelope (what the options endpoint returns) ─────────────────

export interface PresetSummary {
  /** Pipeline tier name, e.g. "premium-legacy". */
  name: string;
  /** Human-readable label for the chip. */
  display_name: string;
  /** "$", "$$", "$$$", "$$$$" — already in YAMLs. */
  cost_indicator?: string;
  /** Short one-liner shown under the preset chip. */
  summary?: string;
  /** The full pipeline doc to load when the preset is selected. */
  doc: PipelineDoc;
}

export interface PipelineManifest {
  /** Source manifest version — used for cache busting / migration. */
  manifest_version: string;
  /** All modules the capability knows about, keyed by canonical name. */
  modules: Record<string, ModuleManifest>;
  /** Built-in presets the user can pick. */
  presets: PresetSummary[];
}

// ── Editor state derived from a manifest + a working doc ──────────────────

export type EditorMode = 'preset' | 'modified' | 'custom' | 'upload';

export interface EditorState {
  /** The pipeline doc the user is currently editing. */
  doc: PipelineDoc;
  /** Name of the preset this doc was initialized from, if any. */
  basePresetName: string | null;
  /** Derived: pristine vs modified vs custom vs upload-mode. */
  mode: EditorMode;
}

// ── Target metrics — what the cost estimator resolves target.* against ────

/**
 * Asset/target metrics supplied by Guard when estimating cost. Mirrors the
 * spec's "target.*" namespace. Optional fields are filled in best-effort
 * from the asset service; unresolved fields cause the dependent module's
 * estimate to surface as "unavailable" (never silently zero).
 */
export interface TargetMetrics {
  loc?: number;
  file_count?: number;
  function_count?: number;
  /** RAW repository size (pre-filter). Modules without `filter_id`
   *  (pass-through ingests: git-clone, deltascan-repo) drive on this. */
  size_bytes?: number;
  /**
   * Exact post-filter byte counts keyed by `filter_id` from
   * `dist/filter-rules.json`. Modules with a `filter_id` look themselves
   * up here at projection time — the estimator overrides
   * `target.size_bytes` to the matching entry for those modules'
   * driver evaluation. Absent → no projection for filter-aware modules
   * (the asset is in metrics-pending state).
   */
  filtered_bytes_by_filter?: Record<string, number>;
  language?: string;
  languages?: Record<string, number>;
}

// ── Cost-estimate result the panel renders ────────────────────────────────

export interface ComponentEstimate {
  /** Module name in the pipeline doc. */
  module: string;
  /** Stage the module ran in. */
  stage: string;
  /** Friendly component name (for composite leaves). */
  componentName?: string;
  /** Confidence interval bounds in USD. */
  low: number | null;
  p50: number | null;
  high: number | null;
  /** If null, estimate is unavailable. */
  reason?:
    | 'missing_calibration'
    | 'driver_unresolved'
    | 'no_cost_model'
    /** Ensemble component's `models` field couldn't resolve to a non-empty list. */
    | 'ensemble_unresolved';
}

export interface StageEstimate {
  stage: string;
  low: number;
  p50: number;
  high: number;
  components: ComponentEstimate[];
  /** Components whose cost couldn't be estimated. */
  unavailable: ComponentEstimate[];
}

export interface PipelineEstimate {
  stages: StageEstimate[];
  total: { low: number; p50: number; high: number };
  /** Pipeline-level budget cap from the doc, in USD. */
  budget?: number;
  /** True when the projected p50 total exceeds the budget cap (run will likely truncate at budget). */
  exceedsBudget?: boolean;
  /** True if any module's cost was unavailable. */
  hasUnavailable: boolean;
}
